import { useState } from "react";
import { Download, Loader2, Megaphone, Users2, ArrowUpDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BulkGrantCreditsPanel } from "./billing/BulkGrantCreditsPanel";
import { BulkGrantHistoryPanel } from "./billing/BulkGrantHistoryPanel";
import { TopupBackfillPanel } from "./billing/TopupBackfillPanel";
import { AiCreditCostsPanel } from "./billing/AiCreditCostsPanel";
const TIER_OPTIONS = ["starter", "plus", "pro", "elite", "agency", "comp", "admin_workspace", "super_admin"] as const;

const parseUserIds = (raw: string): string[] => {
  return raw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter((s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s));
};

function ExportPanel() {
  const [busy, setBusy] = useState(false);
  const handleExport = async () => {
    setBusy(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-bulk-export-users`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `users-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="h-5 w-5" /> Bulk export users
        </CardTitle>
        <CardDescription>
          Download a CSV of every user with email, tier, signup date, last sign-in, subscription status, MRR estimate, and workspace count.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={handleExport} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export CSV
        </Button>
      </CardContent>
    </Card>
  );
}

function AnnouncementPanel() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [userIdsRaw, setUserIdsRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const targets = parseUserIds(userIdsRaw);
  const handlePublish = async () => {
    if (!title.trim() || !body.trim() || targets.length === 0) {
      toast.error("Title, body, and at least one valid user ID are required");
      return;
    }
    setBusy(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const uid = session?.session?.user?.id;
      if (!uid) throw new Error("Not authenticated");
      const { error } = await supabase.from("admin_announcements").insert({
        title: title.trim(),
        body_md: body.trim(),
        published_at: new Date().toISOString(),
        audience: "all",
        kind: "general",
        severity: "info",
        created_by_user_id: uid,
        target_user_ids: targets,
      });
      if (error) throw error;
      await supabase.from("admin_actions").insert({
        admin_user_id: uid,
        action: "bulk_announcement_targeted",
        metadata: { title, target_count: targets.length },
      });
      toast.success(`Published to ${targets.length} user(s)`);
      setTitle("");
      setBody("");
      setUserIdsRaw("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Publish failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="h-5 w-5" /> Targeted announcement
        </CardTitle>
        <CardDescription>
          Publish an in-app announcement visible only to a specific list of users. No external email is sent.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
        </div>
        <div>
          <Label>Body (markdown)</Label>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} maxLength={5000} />
        </div>
        <div>
          <Label>User IDs (comma, newline, or space separated)</Label>
          <Textarea
            value={userIdsRaw}
            onChange={(e) => setUserIdsRaw(e.target.value)}
            rows={3}
            placeholder="00000000-0000-0000-0000-000000000000"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {targets.length} valid UUID{targets.length === 1 ? "" : "s"} detected.
          </p>
        </div>
        <Button onClick={handlePublish} disabled={busy || targets.length === 0}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />}
          Publish to {targets.length || 0} user(s)
        </Button>
      </CardContent>
    </Card>
  );
}

function TierMigrationPanel() {
  const [tier, setTier] = useState<string>("");
  const [userIdsRaw, setUserIdsRaw] = useState("");
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const targets = parseUserIds(userIdsRaw);

  const run = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-bulk-tier-migrate", {
        body: { target_tier: tier, user_ids: targets, reason },
      });
      if (error) throw error;
      const result = data as { succeeded: number; failed: number };
      toast.success(`Migrated: ${result.succeeded} ok, ${result.failed} failed`);
      setUserIdsRaw("");
      setReason("");
      setOpen(false);
      setConfirmText("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Migration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowUpDown className="h-5 w-5" /> Bulk tier migration
        </CardTitle>
        <CardDescription>
          Move a list of users to a new tier. Each change writes an audit entry. Super-admin only.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Target tier</Label>
          <Select value={tier} onValueChange={setTier}>
            <SelectTrigger><SelectValue placeholder="Choose tier" /></SelectTrigger>
            <SelectContent>
              {TIER_OPTIONS.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>User IDs</Label>
          <Textarea value={userIdsRaw} onChange={(e) => setUserIdsRaw(e.target.value)} rows={3} />
          <p className="mt-1 text-xs text-muted-foreground">{targets.length} valid UUID(s).</p>
        </div>
        <div>
          <Label>Reason (Audit Log)</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
        </div>
        <Button
          variant="destructive"
          disabled={!tier || targets.length === 0 || !reason.trim()}
          onClick={() => setOpen(true)}
        >
          <AlertTriangle className="h-4 w-4" /> Migrate {targets.length} user(s) to {tier || "…"}
        </Button>
      </CardContent>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm bulk tier migration</AlertDialogTitle>
            <AlertDialogDescription>
              This will change the account tier for {targets.length} user(s) to <b>{tier}</b>. Type <b>MIGRATE</b> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="MIGRATE" />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmText !== "MIGRATE" || busy}
              onClick={(e) => {
                e.preventDefault();
                void run();
              }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export function BulkOpsTab() {
  return (
    <div className="space-y-4">
      <ExportPanel />
      <AnnouncementPanel />
      <BulkGrantCreditsPanel />
      <BulkGrantHistoryPanel />
      <TopupBackfillPanel />
      <AiCreditCostsPanel />
      <TierMigrationPanel />
      <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
        <Users2 className="mr-1 inline h-3 w-3" />
        Targeted announcements stay in-app only. Excerptus does not send external broadcast email.
      </div>
    </div>
  );
}
